import { useState, useEffect } from "react"
import { useApp } from "../context/useApp"
import Swal from "sweetalert2"
import { Link } from "react-router-dom"
import {
  getOccupancyReport,
  getCitiesDistribution,
  getPropertyTypesDistribution,
  getPropertiesByOwner,
  getEarningsByOwner,
  getAdminEarnings,
} from "../actions/report-actions"
import { stringToCurrency } from "../components/StringToCurrency"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js"
import { Pie, Bar } from "react-chartjs-2"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
)

function Reports() {
  const { user, setLoading } = useApp()
  const [occupancyData, setOccupancyData] = useState(null)
  const [citiesData, setCitiesData] = useState(null)
  const [propertyTypesData, setPropertyTypesData] = useState(null)
  const [ownerPropertiesData, setOwnerPropertiesData] = useState(null)
  const [ownerEarningsData, setOwnerEarningsData] = useState(null)
  const [adminEarningsData, setAdminEarningsData] = useState(null)
  const [expandedOwners, setExpandedOwners] = useState({})
  const [expandedAdmins, setExpandedAdmins] = useState({})

  const toggleOwner = (index) => {
    setExpandedOwners((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const toggleAdmin = (index) => {
    setExpandedAdmins((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  useEffect(() => {
    loadReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadReports = async () => {
    try {
      setLoading(true)

      const [
        occupancy,
        cities,
        propertyTypes,
        ownerProperties,
        ownerEarnings,
        adminEarnings,
      ] = await Promise.all([
        getOccupancyReport(user.token),
        getCitiesDistribution(user.token),
        getPropertyTypesDistribution(user.token),
        getPropertiesByOwner(user.token),
        getEarningsByOwner(user.token),
        getAdminEarnings(user.token),
      ])

      if (occupancy?.code === "00") {
        setOccupancyData(occupancy.data)
      }
      if (cities?.code === "00") {
        setCitiesData(cities.data)
      }
      if (propertyTypes?.code === "00") {
        setPropertyTypesData(propertyTypes.data)
      }
      if (ownerProperties?.code === "00") {
        setOwnerPropertiesData(ownerProperties.data)
      }
      if (ownerEarnings?.code === "00") {
        setOwnerEarningsData(ownerEarnings.data)
      }
      if (adminEarnings?.code === "00") {
        setAdminEarningsData(adminEarnings.data)
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        confirmButtonColor: "#76b4ff",
        text: "No se pudieron cargar los reportes",
      })
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Datos para el gráfico de ocupación
  const occupancyChartData = occupancyData && {
    labels: ["Arrendados", "Sin arrendar"],
    datasets: [
      {
        data: [occupancyData.rented, occupancyData.unrented],
        backgroundColor: ["#7AAACE", "#D4D9C8"],
        borderColor: ["#5A8FB8", "#B8BDA8"],
        borderWidth: 2,
      },
    ],
  }

  // Datos para el gráfico de distribución por ciudad
  const citiesChartData = citiesData && {
    labels: citiesData.map((c) => c.city),
    datasets: [
      {
        label: "Cantidad de inmuebles",
        data: citiesData.map((c) => c.count),
        backgroundColor: [
          "#355872",
          "#7AAACE",
          "#9CD5FF",
          "#F7F8F0",
          "#2F3E52",
          "#D4D9C8",
        ],
        borderColor: [
          "#233A4D",
          "#5A8FB8",
          "#6FB8E6",
          "#E0E5D0",
          "#1E2A38",
          "#B8BDA8",
        ],
        borderWidth: 2,
      },
    ],
  }

  // Datos para el gráfico de distribución por tipo de inmueble
  const propertyTypesChartData = propertyTypesData && {
    labels: propertyTypesData.map((t) => t.type),
    datasets: [
      {
        data: propertyTypesData.map((t) => t.count),
        backgroundColor: [
          "#355872",
          "#7AAACE",
          "#9CD5FF",
          "#F7F8F0",
          "#2F3E52",
          "#D4D9C8",
          "#7AAACE",
          "#9CD5FF",
        ],
        borderColor: [
          "#233A4D",
          "#5A8FB8",
          "#6FB8E6",
          "#E0E5D0",
          "#1E2A38",
          "#B8BDA8",
          "#5A8FB8",
          "#6FB8E6",
        ],
        borderWidth: 2,
      },
    ],
  }

  const occupancyOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((context.parsed / total) * 100).toFixed(1)
            return `${context.label}: ${context.parsed} (${percentage}%)`
          },
        },
      },
    },
  }

  const propertyTypesOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((context.parsed / total) * 100).toFixed(1)
            return `${context.label}: ${context.parsed} (${percentage}%)`
          },
        },
      },
    },
  }

  const citiesOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  return (
    <div className="w-100 d-flex">
      <div className="container d-flex flex-column">
        <div className="w-100 mt-5">
          <Link to="/inmuebles" className="back-button">
            &#60; Volver
          </Link>
        </div>
        <h1 className="my-5">Reportes de Inmuebles</h1>

        {/* Sección de Reportes */}
        <div className="w-100">
          {/* 1. Pie de Ocupación */}
          <div className="mb-5">
            <h3 className="mb-4">Inmuebles Arrendados vs Sin Arrendar</h3>
            <div
              className="d-flex justify-content-center"
              style={{
                backgroundColor: "#f8f9fa",
                padding: "20px",
                borderRadius: "10px",
              }}
            >
              {occupancyChartData ? (
                <div
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                    minHeight: "350px",
                  }}
                >
                  <Pie data={occupancyChartData} options={occupancyOptions} />
                </div>
              ) : (
                <p>Cargando datos...</p>
              )}
            </div>
            {occupancyData && (
              <div className="row mt-3">
                <div className="col-md-3">
                  <div
                    className="p-3 text-center"
                    style={{
                      backgroundColor: "#e3f2fd",
                      borderRadius: "8px",
                      border: "2px solid #76b4ff",
                    }}
                  >
                    <h5>Arrendados</h5>
                    <h3 style={{ color: "#76b4ff" }}>{occupancyData.rented}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="p-3 text-center"
                    style={{
                      backgroundColor: "#ffebee",
                      borderRadius: "8px",
                      border: "2px solid #ff6b6b",
                    }}
                  >
                    <h5>Sin Arrendar</h5>
                    <h3 style={{ color: "#ff6b6b" }}>
                      {occupancyData.unrented}
                    </h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="p-3 text-center"
                    style={{
                      backgroundColor: "#f0f4ff",
                      borderRadius: "8px",
                      border: "2px solid #5a9fe6",
                    }}
                  >
                    <h5>Total</h5>
                    <h3 style={{ color: "#5a9fe6" }}>{occupancyData.total}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div
                    className="p-3 text-center"
                    style={{
                      backgroundColor: "#f0fff4",
                      borderRadius: "8px",
                      border: "2px solid #51cf66",
                    }}
                  >
                    <h5>Ocupación</h5>
                    <h3 style={{ color: "#51cf66" }}>
                      {occupancyData.total > 0
                        ? (
                            (occupancyData.rented / occupancyData.total) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </h3>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 2. Gráfico de Distribución por Ciudad */}
          <div className="mb-5">
            <h3 className="mb-4">Distribución de Inmuebles por Ciudad</h3>
            <div
              className="d-flex justify-content-center"
              style={{
                backgroundColor: "#f8f9fa",
                padding: "20px",
                borderRadius: "10px",
              }}
            >
              {citiesChartData ? (
                <div
                  style={{ width: "100%", maxWidth: "600px", height: "400px" }}
                >
                  <Bar data={citiesChartData} options={citiesOptions} />
                </div>
              ) : (
                <p>Cargando datos...</p>
              )}
            </div>
          </div>

          {/* 3. Gráfico de Distribución por Tipo de Inmueble */}
          <div className="mb-5">
            <h3 className="mb-4">Distribución de Inmuebles por Tipo</h3>
            <div
              className="d-flex justify-content-center"
              style={{
                backgroundColor: "#f8f9fa",
                padding: "20px",
                borderRadius: "10px",
              }}
            >
              {propertyTypesChartData ? (
                <div
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                    minHeight: "350px",
                  }}
                >
                  <Pie
                    data={propertyTypesChartData}
                    options={propertyTypesOptions}
                  />
                </div>
              ) : (
                <p>Cargando datos...</p>
              )}
            </div>
          </div>

          {/* 4. Inmuebles por Propietario */}
          <div className="mb-5">
            <h3 className="mb-4">Distribución de Inmuebles por Propietario</h3>
            {ownerPropertiesData &&
            ownerPropertiesData.details &&
            ownerPropertiesData.details.length > 0 ? (
              <>
                <div
                  className="d-flex justify-content-center"
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "20px",
                    borderRadius: "10px",
                    marginBottom: "20px",
                  }}
                >
                  {ownerPropertiesData.chartData && (
                    <div
                      style={{
                        width: "100%",
                        maxHeight: "600px",
                        overflow: "auto",
                      }}
                    >
                      <div
                        style={{
                          width: "700px",
                          height: "600px",
                          margin: "0 auto",
                        }}
                      >
                        <Pie
                          data={{
                            labels: ownerPropertiesData.chartData.map(
                              (o) => o.ownerName,
                            ),
                            datasets: [
                              {
                                data: ownerPropertiesData.chartData.map(
                                  (o) => o.count,
                                ),
                                backgroundColor: [
                                  "#355872",
                                  "#7AAACE",
                                  "#9CD5FF",
                                  "#F7F8F0",
                                  "#2F3E52",
                                  "#D4D9C8",
                                  "#7AAACE",
                                  "#F7F8F0",
                                ],
                                borderColor: [
                                  "#233A4D",
                                  "#5A8FB8",
                                  "#6FB8E6",
                                  "#E0E5D0",
                                  "#1E2A38",
                                  "#B8BDA8",
                                  "#5A8FB8",
                                  "#E0E5D0",
                                ],
                                borderWidth: 2,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                position: "right",
                                labels: {
                                  padding: 15,
                                  font: {
                                    size: 12,
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <h5 className="mb-3">Detalles por Propietario</h5>
                  {ownerPropertiesData.details.map((owner, ownerIndex) => {
                    // Encontrar datos de ganancias para este propietario
                    const earningsForOwner = ownerEarningsData?.find(
                      (e) => e.ownerName === owner.ownerName,
                    )

                    // Crear mapa de ganancias por propiedad (address como key)
                    const earningsMap = {}
                    if (earningsForOwner?.properties) {
                      earningsForOwner.properties.forEach((prop) => {
                        earningsMap[prop.address] = prop
                      })
                    }

                    return (
                      <div
                        key={ownerIndex}
                        className="mb-4"
                        style={{
                          backgroundColor: "#f8f9fa",
                          padding: "15px",
                          borderRadius: "8px",
                          border: "1px solid #dee2e6",
                        }}
                      >
                        {/* Header Clickeable */}
                        <div
                          onClick={() => toggleOwner(ownerIndex)}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: expandedOwners[ownerIndex]
                              ? "15px"
                              : 0,
                            cursor: "pointer",
                            userSelect: "none",
                            padding: "10px",
                            borderRadius: "6px",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#e8ecf1")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flex: 1,
                            }}
                          >
                            <span
                              style={{
                                transition: "transform 0.2s",
                                transform: expandedOwners[ownerIndex]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                fontSize: "1.2em",
                                color: "#76b4ff",
                              }}
                            >
                              ▼
                            </span>
                            <h6 style={{ margin: 0, color: "#495057" }}>
                              <strong>{owner.ownerName}</strong>
                            </h6>
                          </div>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#76b4ff",
                              color: "white",
                              fontSize: "0.9em",
                              padding: "5px 10px",
                            }}
                          >
                            {owner.count} inmueble{owner.count > 1 ? "s" : ""}
                          </span>
                        </div>

                        {/* Contenido Colapsable */}
                        {expandedOwners[ownerIndex] && (
                          <>
                            {/* Lista de inmuebles */}
                            <div>
                              {owner.properties.map((prop, propIndex) => {
                                const earnings = earningsMap[prop.address]
                                return (
                                  <div
                                    key={propIndex}
                                    className="mb-3"
                                    style={{
                                      backgroundColor: "white",
                                      padding: "12px",
                                      borderRadius: "6px",
                                      border: "1px solid #e0e0e0",
                                      borderLeft: "4px solid #76b4ff",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit, minmax(200px, 1fr))",
                                        gap: "12px",
                                      }}
                                    >
                                      {/* Info de la propiedad */}
                                      <div>
                                        <small
                                          style={{
                                            color: "#6c757d",
                                            fontWeight: "600",
                                          }}
                                        >
                                          PROPIEDAD
                                        </small>
                                        <div>
                                          <strong>{prop.address}</strong>
                                        </div>
                                        <small style={{ color: "#999" }}>
                                          {prop.city} • {prop.propertyType}
                                        </small>
                                      </div>

                                      {/* Estado de alquiler */}
                                      <div>
                                        <small
                                          style={{
                                            color: "#6c757d",
                                            fontWeight: "600",
                                          }}
                                        >
                                          ESTADO
                                        </small>
                                        <div>
                                          <span
                                            className="badge"
                                            style={{
                                              backgroundColor: earnings
                                                ? "#51cf66"
                                                : "#ff6b6b",
                                              color: "white",
                                              padding: "6px 12px",
                                              fontSize: "0.85em",
                                            }}
                                          >
                                            {earnings
                                              ? "Alquilado"
                                              : "No alquilado"}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>

                            {/* Resumen de alquilados vs no alquilados */}
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "12px",
                                marginTop: "12px",
                              }}
                            >
                              {/* Tarjeta verde - Alquilados */}
                              <div
                                style={{
                                  backgroundColor: "#f0fff4",
                                  padding: "12px",
                                  borderRadius: "6px",
                                  border: "1px solid #51cf66",
                                  textAlign: "center",
                                }}
                              >
                                <small
                                  style={{
                                    color: "#51cf66",
                                    fontWeight: "600",
                                  }}
                                >
                                  ALQUILADOS
                                </small>
                                <div>
                                  <strong
                                    style={{
                                      color: "#2d9c4b",
                                      fontSize: "1.3em",
                                    }}
                                  >
                                    {
                                      owner.properties.filter(
                                        (p) => earningsMap[p.address],
                                      ).length
                                    }
                                  </strong>
                                </div>
                              </div>

                              {/* Tarjeta roja - No alquilados */}
                              <div
                                style={{
                                  backgroundColor: "#ffe0e0",
                                  padding: "12px",
                                  borderRadius: "6px",
                                  border: "1px solid #ff6b6b",
                                  textAlign: "center",
                                }}
                              >
                                <small
                                  style={{
                                    color: "#ff6b6b",
                                    fontWeight: "600",
                                  }}
                                >
                                  NO ALQUILADOS
                                </small>
                                <div>
                                  <strong
                                    style={{
                                      color: "#c92a2a",
                                      fontSize: "1.3em",
                                    }}
                                  >
                                    {
                                      owner.properties.filter(
                                        (p) => !earningsMap[p.address],
                                      ).length
                                    }
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #ffc107",
                }}
              >
                <p>No hay propietarios con inmuebles.</p>
              </div>
            )}
          </div>

          {/* 6. Ganancias Mensuales por Propietario */}
          <div className="mb-5">
            <h3 className="mb-4">Ganancias Mensuales por Propietario</h3>
            {ownerEarningsData && ownerEarningsData.length > 0 ? (
              <div>
                {ownerEarningsData.map((owner, ownerIndex) => (
                  <div
                    key={ownerIndex}
                    className="mb-4"
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    {/* Header Clickeable */}
                    <div
                      onClick={() => toggleOwner(ownerIndex)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: expandedOwners[ownerIndex] ? "15px" : 0,
                        cursor: "pointer",
                        userSelect: "none",
                        padding: "10px",
                        borderRadius: "6px",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e8ecf1")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flex: 1,
                        }}
                      >
                        <span
                          style={{
                            transition: "transform 0.2s",
                            transform: expandedOwners[ownerIndex]
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            fontSize: "1.2em",
                            color: "#76b4ff",
                          }}
                        >
                          ▼
                        </span>
                        <h6 style={{ margin: 0, color: "#495057" }}>
                          <strong>{owner.ownerName}</strong>
                        </h6>
                      </div>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#76b4ff",
                          color: "white",
                          fontSize: "0.9em",
                          padding: "5px 10px",
                        }}
                      >
                        {owner.properties.length} inmueble
                        {owner.properties.length > 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Contenido Colapsable */}
                    {expandedOwners[ownerIndex] && (
                      <>
                        {/* Lista de inmuebles */}
                        <div>
                          {owner.properties.map((prop, propIndex) => (
                            <div
                              key={propIndex}
                              className="mb-3"
                              style={{
                                backgroundColor: "white",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #e0e0e0",
                                borderLeft: "4px solid #51cf66",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(180px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                {/* Propiedad */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    PROPIEDAD
                                  </small>
                                  <div>
                                    <strong>{prop.address}</strong>
                                  </div>
                                  <small style={{ color: "#999" }}>
                                    {prop.city} • {prop.propertyType}
                                  </small>
                                </div>

                                {/* Renta Mensual */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    RENTA MENSUAL
                                  </small>
                                  <div>
                                    <strong style={{ color: "#495057" }}>
                                      {stringToCurrency(prop.monthlyEarning)}
                                    </strong>
                                  </div>
                                </div>

                                {/* Comisión Admin */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    COMISIÓN ADMIN
                                  </small>
                                  <div>
                                    <strong style={{ color: "#ff6b6b" }}>
                                      -{stringToCurrency(prop.adminCommission)}
                                    </strong>
                                  </div>
                                </div>

                                {/* Ganancia Neta */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    GANANCIA NETA
                                  </small>
                                  <div>
                                    <strong style={{ color: "#51cf66" }}>
                                      {stringToCurrency(prop.netEarning)}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Total para este propietario */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                            marginTop: "12px",
                          }}
                        >
                          {/* Tarjeta roja - Total Comisiones */}
                          <div
                            style={{
                              backgroundColor: "#ffe0e0",
                              padding: "12px",
                              borderRadius: "6px",
                              border: "1px solid #ff6b6b",
                              textAlign: "right",
                            }}
                          >
                            <small
                              style={{
                                color: "#ff6b6b",
                                fontWeight: "600",
                              }}
                            >
                              TOTAL COMISIONES
                            </small>
                            <div>
                              <strong
                                style={{
                                  color: "#c92a2a",
                                  fontSize: "1.1em",
                                }}
                              >
                                {stringToCurrency(
                                  owner.properties.reduce(
                                    (sum, prop) => sum + prop.adminCommission,
                                    0,
                                  ),
                                )}
                              </strong>
                            </div>
                          </div>

                          {/* Tarjeta verde - Total Ganancia Neta */}
                          <div
                            style={{
                              backgroundColor: "#f0fff4",
                              padding: "12px",
                              borderRadius: "6px",
                              border: "1px solid #51cf66",
                              textAlign: "right",
                            }}
                          >
                            <small
                              style={{
                                color: "#51cf66",
                                fontWeight: "600",
                              }}
                            >
                              TOTAL GANANCIA NETA
                            </small>
                            <div>
                              <strong
                                style={{
                                  color: "#2d9c4b",
                                  fontSize: "1.1em",
                                }}
                              >
                                {stringToCurrency(
                                  owner.properties.reduce(
                                    (sum, prop) => sum + prop.netEarning,
                                    0,
                                  ),
                                )}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #ffc107",
                }}
              >
                <p>No hay ganancias por propietario.</p>
              </div>
            )}
          </div>

          {/* 7. Ganancias por Administrador */}
          <div className="mb-5">
            <h3 className="mb-4">
              Ganancias de Administradores (Comisión por Gestión)
            </h3>
            {adminEarningsData && adminEarningsData.length > 0 ? (
              <div>
                {adminEarningsData.map((admin, adminIndex) => (
                  <div
                    key={adminIndex}
                    className="mb-4"
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    {/* Header Clickeable */}
                    <div
                      onClick={() => toggleAdmin(adminIndex)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: expandedAdmins[adminIndex] ? "15px" : 0,
                        cursor: "pointer",
                        userSelect: "none",
                        padding: "10px",
                        borderRadius: "6px",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e8f5e9")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flex: 1,
                        }}
                      >
                        <span
                          style={{
                            transition: "transform 0.2s",
                            transform: expandedAdmins[adminIndex]
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            fontSize: "1.2em",
                            color: "#51cf66",
                          }}
                        >
                          ▼
                        </span>
                        <h6 style={{ margin: 0, color: "#495057" }}>
                          <strong>{admin.adminName}</strong>
                        </h6>
                      </div>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#51cf66",
                          color: "white",
                          fontSize: "0.9em",
                          padding: "5px 10px",
                        }}
                      >
                        {admin.properties.length} inmueble
                        {admin.properties.length > 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Contenido Colapsable */}
                    {expandedAdmins[adminIndex] && (
                      <>
                        {/* Lista de inmuebles */}
                        <div>
                          {admin.properties.map((prop, propIndex) => (
                            <div
                              key={propIndex}
                              className="mb-3"
                              style={{
                                backgroundColor: "white",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #e0e0e0",
                                borderLeft: "4px solid #51cf66",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(180px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                {/* Propiedad */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    PROPIEDAD
                                  </small>
                                  <div>
                                    <strong>{prop.address}</strong>
                                  </div>
                                  <small style={{ color: "#999" }}>
                                    {prop.city} • {prop.propertyType}
                                  </small>
                                </div>

                                {/* Canon Mensual */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    CANON MENSUAL
                                  </small>
                                  <div>
                                    <strong style={{ color: "#495057" }}>
                                      {stringToCurrency(
                                        prop.monthlyEarning || 0,
                                      )}
                                    </strong>
                                  </div>
                                </div>

                                {/* Porcentaje */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    PORCENTAJE COMISIÓN
                                  </small>
                                  <div>
                                    <strong style={{ color: "#495057" }}>
                                      {prop.managementPercentage}%
                                    </strong>
                                  </div>
                                </div>

                                {/* Comisión */}
                                <div>
                                  <small
                                    style={{
                                      color: "#6c757d",
                                      fontWeight: "600",
                                    }}
                                  >
                                    COMISIÓN
                                  </small>
                                  <div>
                                    <strong style={{ color: "#51cf66" }}>
                                      {stringToCurrency(prop.adminFee)}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Total para este administrador */}
                        <div
                          style={{
                            backgroundColor: "#f0fff4",
                            padding: "12px",
                            borderRadius: "6px",
                            border: "1px solid #51cf66",
                            marginTop: "12px",
                            textAlign: "right",
                          }}
                        >
                          <small
                            style={{
                              color: "#51cf66",
                              fontWeight: "600",
                            }}
                          >
                            TOTAL COMISIONES
                          </small>
                          <div>
                            <strong
                              style={{ color: "#2d9c4b", fontSize: "1.1em" }}
                            >
                              {stringToCurrency(admin.totalAdminFee)}
                            </strong>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #ffc107",
                }}
              >
                <p>No hay comisiones de administración.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Reports
